import React, { createContext, useContext, useState, ReactNode, useRef, useEffect, useCallback } from 'react';
import { useViewSize } from './ViewSizeContext';
import { useClientMount } from './ClientMountProvider';

// -------------------- Constants

// Default volume level for all audio/video elements (0-1 range)
export const DEFAULT_GLOBAL_VOLUME = 0.75;

// LocalStorage key for persisting volume setting
export const VOLUME_STORAGE_KEY = 'globalVolume';

// LocalStorage key for persisting sound denial
export const SOUND_DENIAL_KEY = 'soundDenied';

// SessionStorage key for persisting sound approval (current session only)
export const SOUND_APPROVAL_KEY = 'soundApproved';

// -------------------- SoundContext and Provider

interface SoundContextType {
  soundEnabled: boolean;
  rrtvSoundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  hasInteracted: boolean;
  setHasInteracted: (interacted: boolean) => void;
  globalVolume: number;
  setGlobalVolume: (volume: number) => void;
  registerAudioElement: (id: string, element: HTMLAudioElement | HTMLVideoElement) => void;
  unregisterAudioElement: (id: string) => void;
}

const SoundContext = createContext<SoundContextType | undefined>(undefined);

interface SoundProviderProps {
  children: ReactNode;
}

// -------------------- SoundProvider component

export const SoundProvider: React.FC<SoundProviderProps> = ({ children }) => {
  const { isDesktop, isReady } = useViewSize();
  const { mounted } = useClientMount();

  // Load initial volume from localStorage or default to DEFAULT_GLOBAL_VOLUME
  const [globalVolume, setGlobalVolumeState] = useState<number>(() => {
    if (typeof window !== 'undefined') {
      const savedVolume = localStorage.getItem(VOLUME_STORAGE_KEY);
      return savedVolume ? parseFloat(savedVolume) : DEFAULT_GLOBAL_VOLUME;
    }
    return DEFAULT_GLOBAL_VOLUME;
  });

  // Default soundEnabled to true on mobile/tablet, false on desktop
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(false);
  const [rrtvSoundEnabled, setRrtvSoundEnabled] = useState<boolean>(false);
  const [hasInteracted, setHasInteracted] = useState<boolean>(false);
  const hasInitializedSound = useRef(false);

  // Check if user has previously denied sound
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Only check for persistent denial (localStorage)
    // We don't use sessionStorage for approvals because:
    // 1. React Context maintains sound state during SPA navigation
    // 2. On refresh, browsers block autoplay anyway, so we need to prompt again
    const hasDenied = localStorage.getItem(SOUND_DENIAL_KEY) === 'true';

    if (hasDenied) {
      setHasInteracted(true);
    }
  }, []);

  // Set initial soundEnabled based on device type (only once on mount)
  useEffect(() => {
    if (!hasInitializedSound.current && isReady && mounted) {
      setRrtvSoundEnabled(!isDesktop); // true for mobile/tablet, false for desktop
      hasInitializedSound.current = true;
    }
  }, [isDesktop, isReady, mounted]);

  // Registry to track all audio/video elements
  const audioElementsRef = useRef<Map<string, HTMLAudioElement | HTMLVideoElement>>(new Map());

  // Update soundEnabled and persist user choice
  const setSoundEnabled = useCallback((enabled: boolean) => {
    setSoundEnabledState(enabled);
    if (isDesktop) setRrtvSoundEnabled(enabled);

    if (typeof window === 'undefined') return;

    if (enabled) {
      // User enabled sound - clear any previous denial from localStorage
      localStorage.removeItem(SOUND_DENIAL_KEY);
      // Note: We don't save approval to sessionStorage
      // React Context maintains state during navigation, and on refresh
      // we want to prompt again (since browsers block autoplay anyway)
    } else {
      // User denied/disabled sound - save to localStorage (persistent)
      localStorage.setItem(SOUND_DENIAL_KEY, 'true');
    }
  }, [isDesktop]);

  // Set global volume and persist to localStorage
  const setGlobalVolume = useCallback((newVolume: number) => {
    // Clamp volume between 0 and 1
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setGlobalVolumeState(clampedVolume);

    // Save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem(VOLUME_STORAGE_KEY, clampedVolume.toString());
    }

    // Update all registered audio/video elements
    audioElementsRef.current.forEach((element) => {
      try {
        element.volume = clampedVolume;
      } catch (error) {
        console.warn('Failed to set volume on element:', error);
      }
    });
  }, []);

  // Register an audio/video element
  const registerAudioElement = useCallback((id: string, element: HTMLAudioElement | HTMLVideoElement) => {
    audioElementsRef.current.set(id, element);
    // Set initial volume on the element
    try {
      element.volume = globalVolume;
    } catch (error) {
      console.warn('Failed to set initial volume on element:', error);
    }
  }, [globalVolume]);

  // Unregister an audio/video element
  const unregisterAudioElement = useCallback((id: string) => {
    audioElementsRef.current.delete(id);
  }, []);

  // Update volume on all elements when volume changes
  useEffect(() => {
    audioElementsRef.current.forEach((element) => {
      try {
        element.volume = globalVolume;
      } catch (error) {
        console.warn('Failed to update volume on element:', error);
      }
    });
  }, [globalVolume]);

  // Context value
  const value: SoundContextType = {
    soundEnabled,
    rrtvSoundEnabled,
    setSoundEnabled,
    hasInteracted,
    setHasInteracted,
    globalVolume,
    setGlobalVolume,
    registerAudioElement,
    unregisterAudioElement,
  };

  // Provide context value
  return (
    <SoundContext.Provider value={value}>
      {children}
    </SoundContext.Provider>
  );
};

export const useSoundContext = (): SoundContextType => {
  const context = useContext(SoundContext);
  return context || {
    soundEnabled: false,
    rrtvSoundEnabled: false,
    setSoundEnabled: () => {},
    hasInteracted: false,
    setHasInteracted: () => {},
    globalVolume: DEFAULT_GLOBAL_VOLUME,
    setGlobalVolume: () => {},
    registerAudioElement: () => {},
    unregisterAudioElement: () => {},
  };
};
